import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { MapPin, Phone, Mail, Clock, Send, MessageCircle, AlertCircle } from 'lucide-react';
import { companyInfo, partners } from '../../data/mock';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    civility: '',
    name: '',
    email: '',
    phone: '',
    bank: '',
    cardType: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Votre demande a été envoyée avec succès !', {
      description: 'Nous vous contacterons dans les 48h.',
    });
    setFormData({
      civility: '',
      name: '',
      email: '',
      phone: '',
      bank: '',
      cardType: '',
      message: ''
    });
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left - Contact Info */}
          <div>
            <span className="text-[#6B2C91] font-semibold text-sm uppercase tracking-wider">
              Contact
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Contactez-Nous
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Notre équipe est à votre disposition pour répondre à toutes vos questions 
              et vous accompagner dans votre expérience KIPAY.
            </p>

            {/* Contact Cards */}
            <div className="space-y-4 mb-8">
              <Card className="p-4 border-0 shadow-md flex items-start gap-4">
                <div className="w-12 h-12 bg-[#6B2C91]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#6B2C91]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Adresse</h3>
                  <p className="text-gray-600 text-sm">{companyInfo.contact.address}</p>
                </div>
              </Card>

              <Card className="p-4 border-0 shadow-md flex items-start gap-4">
                <div className="w-12 h-12 bg-[#6B2C91]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#6B2C91]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Téléphone</h3>
                  <p className="text-gray-600 text-sm">Service Client : {companyInfo.contact.phone}</p>
                  <p className="text-gray-600 text-sm">Entreprises : {companyInfo.contact.businessPhone}</p>
                </div>
              </Card>

              <Card className="p-4 border-0 shadow-md flex items-start gap-4">
                <div className="w-12 h-12 bg-[#6B2C91]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-[#6B2C91]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Email</h3>
                  <p className="text-gray-600 text-sm">{companyInfo.contact.email}</p>
                  <p className="text-gray-600 text-sm">{companyInfo.contact.supportEmail}</p>
                </div>
              </Card>

              <Card className="p-4 border-0 shadow-md flex items-start gap-4">
                <div className="w-12 h-12 bg-[#6B2C91]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-[#6B2C91]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Horaires</h3>
                  <p className="text-gray-600 text-sm">Lundi - Vendredi : 8h00 - 20h00</p>
                  <p className="text-gray-600 text-sm">Samedi : 9h00 - 17h00</p>
                  <p className="text-gray-600 text-sm">Dimanche : 10h00 - 14h00</p>
                </div>
              </Card>
            </div>

            {/* Emergency */}
            <div className="bg-red-50 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
              <div>
                <h4 className="font-semibold text-red-700">Urgences (Perte/Vol)</h4>
                <p className="text-sm text-red-600">Disponible 24h/24, 7j/7</p>
                <p className="text-sm text-red-600">Ou via l'App KIPAY (1 clic)</p>
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div>
            <Card className="p-8 border-0 shadow-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Commandez Votre Carte KIPAY
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Civility & Name */}
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="civility">Civilité</Label>
                    <Select
                      value={formData.civility}
                      onValueChange={(value) => setFormData({ ...formData, civility: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="M./Mme" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="m">M.</SelectItem>
                        <SelectItem value="mme">Mme</SelectItem>
                        <SelectItem value="mlle">Mlle</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Label htmlFor="name">Nom Complet</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Votre nom complet"
                      className="mt-1"
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="votre@email.ci"
                    className="mt-1"
                    required
                  />
                </div>

                {/* Phone */}
                <div>
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+225 XX XX XX XX XX"
                    className="mt-1"
                    required
                  />
                </div>

                {/* Bank */}
                <div>
                  <Label htmlFor="bank">Banque Partenaire</Label>
                  <Select
                    value={formData.bank}
                    onValueChange={(value) => setFormData({ ...formData, bank: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Sélectionnez votre banque" />
                    </SelectTrigger>
                    <SelectContent>
                      {partners.banks.map((bank, index) => (
                        <SelectItem key={index} value={bank.name}>
                          {bank.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Card Type */}
                <div>
                  <Label htmlFor="cardType">Type de Carte</Label>
                  <Select
                    value={formData.cardType}
                    onValueChange={(value) => setFormData({ ...formData, cardType: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Choisissez votre carte" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bronze">KIPAY Bronze (5 000 FCFA/an)</SelectItem>
                      <SelectItem value="silver">KIPAY Silver (15 000 FCFA/an)</SelectItem>
                      <SelectItem value="gold">KIPAY Gold (50 000 FCFA/an)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Message */}
                <div>
                  <Label htmlFor="message">Message (Optionnel)</Label>
                  <textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Votre message..."
                    rows={3}
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6B2C91] focus:border-transparent resize-none"
                  />
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-[#6B2C91] hover:bg-[#5a2579] text-white"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Envoyer Ma Demande
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  En soumettant ce formulaire, vous acceptez nos conditions générales d'utilisation.
                </p>
              </form>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
